'use client';

import { useReducer, useCallback, useMemo } from 'react';
import { useTRPC } from '@/lib/trpc/client';
import { useMutation } from '@tanstack/react-query';
import {
  MAGIC_BYTES_MAP,
  MAX_FILE_SIZE,
  MAX_TOTAL_UPLOAD_SIZE,
  MAX_FILE_COUNT,
  type AllowedContentType,
} from '@sapling/constants/grants';
import { isAllowedContentType } from '@sapling/contracts/grants';

async function validateMagicBytesClient(
  file: File,
  contentType: AllowedContentType,
): Promise<boolean> {
  const header = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(header);
  const expected = MAGIC_BYTES_MAP[contentType];
  return expected.every((b, i) => bytes[i] === b);
}

export type UploadedFile = {
  uploadId: string;
  fileName: string;
  contentType: AllowedContentType;
  sizeBytes: number;
};

type State = {
  files: UploadedFile[];
  uploading: boolean;
  error: string | null;
};

type Action =
  | { type: 'UPLOAD_START' }
  | { type: 'FILE_UPLOADED'; file: UploadedFile }
  | { type: 'UPLOAD_COMPLETE' }
  | { type: 'UPLOAD_ERROR'; error: string }
  | { type: 'REMOVE_FILE'; uploadId: string };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'UPLOAD_START':
      return { ...state, uploading: true, error: null };
    case 'FILE_UPLOADED':
      return { ...state, files: [...state.files, action.file] };
    case 'UPLOAD_COMPLETE':
      return { ...state, uploading: false };
    case 'UPLOAD_ERROR':
      return { ...state, uploading: false, error: action.error };
    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter((f) => f.uploadId !== action.uploadId),
      };
  }
}

export function useGrantApplicationUpload() {
  const [state, dispatch] = useReducer(reducer, {
    files: [],
    uploading: false,
    error: null,
  });

  const trpc = useTRPC();
  const getUploadUrl = useMutation(trpc.grants.getUploadUrl.mutationOptions());

  const totalSize = state.files.reduce((sum, f) => sum + f.sizeBytes, 0);

  function validateFiles(files: File[]): string | null {
    if (state.files.length + files.length > MAX_FILE_COUNT) {
      return `Max ${MAX_FILE_COUNT} filer.`;
    }

    for (const file of files) {
      if (state.files.some((f) => f.fileName === file.name)) {
        return `${file.name} är redan uppladdad.`;
      }
      if (!isAllowedContentType(file.type)) {
        return 'Tillåtna filtyper: PDF, JPG, PNG.';
      }
      if (file.size > MAX_FILE_SIZE) {
        return `${file.name} är för stor. Max 5 MB per fil.`;
      }
    }

    const newTotal = totalSize + files.reduce((sum, f) => sum + f.size, 0);
    if (newTotal > MAX_TOTAL_UPLOAD_SIZE) {
      return `Totala filstorleken överstiger ${MAX_TOTAL_UPLOAD_SIZE / (1024 * 1024)} MB.`;
    }

    return null;
  }

  async function upload(files: File[]) {
    const validationError = validateFiles(files);
    if (validationError) {
      dispatch({ type: 'UPLOAD_ERROR', error: validationError });
      return;
    }

    dispatch({ type: 'UPLOAD_START' });

    for (const file of files) {
      try {
        if (!isAllowedContentType(file.type)) {
          throw new Error('Tillåtna filtyper: PDF, JPG, PNG.');
        }

        const contentType = file.type;

        const validMagic = await validateMagicBytesClient(file, contentType);
        if (!validMagic) {
          throw new Error(
            `${file.name} har ogiltigt filformat. Kontrollera att filen verkligen är en PDF, JPG eller PNG.`,
          );
        }

        const {
          uploadId,
          url,
          contentType: ct,
        } = await getUploadUrl.mutateAsync({
          contentType,
          fileName: file.name,
          fileSize: file.size,
        });

        const response = await fetch(url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': ct,
          },
        });

        if (!response.ok && response.status !== 200) {
          throw new Error(`Uppladdning misslyckades: ${file.name}`);
        }

        dispatch({
          type: 'FILE_UPLOADED',
          file: {
            uploadId,
            fileName: file.name,
            contentType,
            sizeBytes: file.size,
          },
        });
      } catch (err) {
        dispatch({
          type: 'UPLOAD_ERROR',
          error:
            err instanceof Error
              ? err.message
              : `Något gick fel vid uppladdning av ${file.name}.`,
        });
        return;
      }
    }

    dispatch({ type: 'UPLOAD_COMPLETE' });
  }

  const removeFile = useCallback((uploadId: string) => {
    dispatch({ type: 'REMOVE_FILE', uploadId });
  }, []);

  const uploads = useMemo(
    () =>
      state.files.map((f) => ({
        uploadId: f.uploadId,
        originalFileName: f.fileName,
        contentType: f.contentType,
      })),
    [state.files],
  );

  return {
    files: state.files,
    uploads,
    uploading: state.uploading,
    error: state.error,
    totalSize,
    upload,
    removeFile,
  };
}
